import { lotusHealthZod } from '@lotushealth/lotushealth_js_sdk';
import { fetchPatientReferrals as fetchPatientReferralsFromSdk } from '@lotushealth/lotushealth_js_sdk/endpoints';
import type {
  InboundCommunicationRecordT,
  InboundCommunicationT,
  EFaxCommunicationPartT,
  FileAttachmentT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { createLotusRequestConfig } from '@/shared/api/lotus';

export interface PatientReferral {
  id: string;
  createdAt: string;
  updatedAt: string;
  communication: {
    direction: 'inbound' | 'outbound';
    id: string;
    patientId: string;
    subject: string;
    body: string | null;
    attachments: FileAttachmentT[];
    methodSpecificPart:
      | { type: 'metriport'; messageId: string; sender: string | null }
      | { type: 'efax'; faxId: string; remoteFaxNumber: string };
    status: string;
    receivedAt: string | null;
    downloadedAt: string | null;
    createdAt: string;
    updatedAt: string;
    errorDetails: string | null;
  };
}

export async function fetchPatientReferrals(
  userId: string,
  signal?: AbortSignal
): Promise<PatientReferral[]> {
  const config = await createLotusRequestConfig({ signal });
  const response = await fetchPatientReferralsFromSdk(userId, config);
  const referrals = lotusHealthZod.fetchPatientReferralsResponse.parse(
    response.data
  ) as InboundCommunicationRecordT[];

  return referrals.map((referral) => {
    if (referral.communication.direction !== 'inbound') {
      throw new Error('Expected patient referral communication to be inbound');
    }

    const communication = referral.communication as InboundCommunicationT;

    return {
      id: referral.id,
      createdAt: referral.created_at,
      updatedAt: referral.updated_at,
      communication: {
        direction: 'inbound',
        id: communication.id,
        patientId: communication.patient_id,
        subject: communication.subject,
        body: communication.body,
        attachments: communication.attachments,
        methodSpecificPart:
          communication.method_specific_part.type === 'metriport'
            ? {
                type: 'metriport',
                messageId: communication.method_specific_part.message_id,
                sender: communication.method_specific_part.sender ?? null,
              }
            : {
                type: 'efax',
                faxId: (communication.method_specific_part as EFaxCommunicationPartT).fax_id,
                remoteFaxNumber: (communication.method_specific_part as EFaxCommunicationPartT)
                  .remote_fax_number,
              },
        status: communication.status,
        receivedAt: communication.received_at,
        downloadedAt: communication.downloaded_at,
        createdAt: communication.created_at,
        updatedAt: communication.updated_at,
        errorDetails: communication.error_details,
      },
    };
  });
}
